// features/dataAssets/dataAssetsApiSlice.js
import { createSelector, createEntityAdapter } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/api/apiSlice';

const dataAssetsAdapter = createEntityAdapter({});

const initialState = dataAssetsAdapter.getInitialState();

const normalizeDataAssets = (responseData) => {
    const loadedDataAssets = Array.isArray(responseData)
        ? responseData.map((dataAsset) => ({
            ...dataAsset,
            id: dataAsset.data_asset_id || dataAsset._id,
        }))
        : [];

    return dataAssetsAdapter.setAll(
        dataAssetsAdapter.getInitialState(),
        loadedDataAssets
    );
};

const provideDataAssetTags = (result) => {
    if (result?.ids) {
        return [
            { type: 'DataAsset', id: 'LIST' },
            ...result.ids.map((id) => ({ type: 'DataAsset', id })),
        ];
    }

    return [{ type: 'DataAsset', id: 'LIST' }];
};

export const dataAssetsApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getDataAssets: builder.query({
            query: () => ({
                url: '/data-assets?view=summary',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result?.isError;
                },
            }),
            transformResponse: normalizeDataAssets,
            providesTags: provideDataAssetTags,
        }),

        getReleaseDataAssets: builder.query({
            query: () => ({
                url: '/data-assets?view=release',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result?.isError;
                },
            }),
            transformResponse: normalizeDataAssets,
            providesTags: provideDataAssetTags,
        }),
        getDataAsset: builder.query({
            query: (id) => `/data-assets/${id}`,
            providesTags: (result, error, id) => [
                { type: 'DataAsset', id },
            ],
        }),

        getDataAssetByName: builder.query({
            query: (name) => ({
                url: '/data-assets/get-by-name',
                method: 'POST',
                body: { name }
            }),
            providesTags: (result, error, arg) => [{ type: 'DataAsset', id: arg }],
        }),
        addNewDataAsset: builder.mutation({
            query: (initialDataAssetData) => ({
                url: '/data-assets',
                method: 'POST',
                body: {
                    ...initialDataAssetData,
                },
            }),
            invalidatesTags: [{ type: 'DataAsset', id: 'LIST' }],
        }),
        updateDataAsset: builder.mutation({
            query: ({ id, ...patch }) => ({
                url: `/data-assets/${id}`,
                method: 'PATCH',
                body: patch,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'DataAsset', id: arg.id },
                { type: 'DataAsset', id: 'LIST' }, // ✅ ensures grids refresh too
            ],
        }),
        deleteDataAsset: builder.mutation({
            query: ({ id }) => ({
                url: `/data-assets`,
                method: 'DELETE',
                body: { id },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'DataAsset', id: arg.id },
            ],
        }),
        getGroupedCompanyOutput: builder.mutation({
            query: ({ username, currentIndex, direction }) => ({
                url: '/data-assets/grouped-company-output',
                method: 'POST',
                body: {
                    username,
                    currentIndex,
                    direction
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'DataAsset', id: 'LIST' }
            ]
        }),
        confirmCompanyDomain: builder.mutation({
            query: ({ username, companyDomain, companyId }) => ({
                url: '/data-assets/confirm-domain',
                method: 'POST',
                body: {
                    username,
                    companyDomain,
                    companyId
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'DataAsset', id: 'LIST' }
            ]
        }),
        getDomainBreakdownByPartnerId: builder.query({
            query: (pid) => ({
                url: `/data-assets/domain-breakdown/${pid}`,
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: 'DataAsset', id: arg }],
        }),
        getDomainBreakdownRange: builder.query({
            query: (days = 1) => ({
                url: '/data-assets/analytics/domain-breakdown',
                method: 'GET',
                params: {
                    days,
                },
            }),
        }),
        getPartnerSummaryByPartnerId: builder.query({
            query: (pid) => ({
                url: `/data-assets/partner-summary/${pid}`,
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: 'DataAsset', id: arg }],
        }),
        removeCompanyDomain: builder.mutation({
            query: ({ username, companyDomain, companyId }) => ({
                url: '/data-assets/remove-domain',
                method: 'POST',
                body: {
                    username,
                    companyDomain,
                    companyId
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'DataAsset', id: 'LIST' }
            ]
        }),
        getUserAgentBreakdown: builder.query({
            query: () => ({
                url: '/data-assets/user-agent-breakdown',
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: 'DataAsset', id: 'USER_AGENT_BREAKDOWN' }],
        }),
        getUserAgentBreakdownByPartnerId: builder.query({
            query: (pid) => ({
                url: `/data-assets/user-agent-breakdown/${pid}`,
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: 'DataAsset', id: arg }],
        }),
        addDataAssetVersions: builder.mutation({
            query: ({ id, versions }) => ({
                url: `/data-assets/${id}/versions`,
                method: 'POST',
                body: { versions },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'DataAsset', id: arg.id },
            ],
        }),
        listCbUsers: builder.query({
            query: ({ org_id }) => ({
                url: `/data-assets/cb/users`,
                method: 'GET',
                params: { org_id },
            }),
            providesTags: (result) =>
                result?.items
                    ? [
                        ...result.items.map((u) => ({ type: 'CbUser', id: u._id })),
                        { type: 'CbUser', id: 'LIST' },
                    ]
                    : [{ type: 'CbUser', id: 'LIST' }],
        }),

        // Create CB user
        createCbUser: builder.mutation({
            query: (body) => ({
                url: `/data-assets/cb/users`,
                method: 'POST',
                body, // { org_id, email, name?, password }
            }),
            invalidatesTags: [{ type: 'CbUser', id: 'LIST' }],
        }),

        // Delete CB user
        deleteCbUser: builder.mutation({
            query: ({ id }) => ({
                url: `/data-assets/cb/users/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'CbUser', id: 'LIST' }],
        }),
    }),
});



export const {
    useGetDataAssetsQuery,
    useGetReleaseDataAssetsQuery,
    useGetDataAssetQuery,
    useGetDataAssetByNameQuery,
    useGetDomainBreakdownByPartnerIdQuery,
    useGetDomainBreakdownRangeQuery,
    useGetPartnerSummaryByPartnerIdQuery,
    useAddNewDataAssetMutation,
    useUpdateDataAssetMutation,
    useDeleteDataAssetMutation,
    useGetGroupedCompanyOutputMutation,
    useConfirmCompanyDomainMutation,
    useRemoveCompanyDomainMutation,
    useGetUserAgentBreakdownQuery,
    useGetUserAgentBreakdownByPartnerIdQuery,
    useAddDataAssetVersionsMutation,
    useListCbUsersQuery,
    useCreateCbUserMutation,
    useDeleteCbUserMutation,
} = dataAssetsApiSlice;

// returns the query result object
export const selectDataAssetsResult = dataAssetsApiSlice.endpoints.getDataAssets.select();

// creates memoized selector
const selectDataAssetsData = createSelector(
    selectDataAssetsResult,
    dataAssetsResult => dataAssetsResult.data // normalized state object with ids & entities
);

// getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllDataAssets,
    selectById: selectDataAssetById,
    selectIds: selectDataAssetIds,
    // Pass in a selector that returns the data assets slice of state
} = dataAssetsAdapter.getSelectors(state => selectDataAssetsData(state) ?? initialState);
